import React from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * OnboardingRouteGuard
 * Protects onboarding wizard routes (/hospital/onboarding/*).
 * Ensures that:
 * 1. An active user token or partner registration session exists.
 * 2. An active, valid applicationId exists and is scoped to the current user.
 * 3. Prevents unauthenticated users or leftover application IDs from loading another person's draft.
 */
export const OnboardingRouteGuard = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // 1. Check for token and user data in storage or context
  const token =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('token') || localStorage.getItem('aps_jwt_token')
      : null;

  const rawUserData =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('aps_user_data')
      : null;

  let currentUser = user;
  if (!currentUser && rawUserData) {
    try {
      currentUser = JSON.parse(rawUserData);
    } catch (e) {
      console.warn('Could not parse stored user data in OnboardingRouteGuard:', e);
    }
  }

  // 2. Check for valid active applicationId
  const urlAppId = searchParams.get('applicationId');
  const storedAppId =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('risa_current_application_id')
      : null;

  const candidateAppId = urlAppId || storedAppId;

  const hasValidAppId =
    typeof candidateAppId === 'string' &&
    candidateAppId.trim().length > 0 &&
    !candidateAppId.startsWith('RISA-APP-');

  // Must have both an authentication token and a valid application ID
  const isAuthorized = Boolean(token && hasValidAppId);

  if (!isAuthorized) {
    // Clean up any stale orphaned application ID so it cannot leak
    if (typeof localStorage !== 'undefined' && !token) {
      localStorage.removeItem('risa_current_application_id');
    }

    const partnerType =
      searchParams.get('type') ||
      (typeof localStorage !== 'undefined' ? localStorage.getItem('risa_partner_type') : null) ||
      'hospital';

    return (
      <Navigate
        to={`/hospital/partner-register?type=${partnerType}`}
        state={{
          from: location,
          warningMessage: 'Please register your organization to access or resume the onboarding application.'
        }}
        replace
      />
    );
  }

  return children;
};

export default OnboardingRouteGuard;
